import { useRef } from 'react';
import { FilePdfIcon, TrashIcon } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldError } from '@/components/ui/field-error';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { EventMaterialsFormSectionProps } from '@/lib/types/event-files.types';
import {
  EVENT_FILE_MAX_COUNT,
  EVENT_FILE_MAX_SIZE_BYTES,
} from '@/lib/types/event-files.types';
import {
  formatEventFileSize,
  getVisibleEventFileCount,
} from '@/lib/utils/event-files.utils';

export function EventMaterialsSection({
  files,
  recurrenceCount,
  isRecurringCreate,
  isLoading = false,
  errors = [],
  onAddFiles,
  onTitleChange,
  onRemove,
  onRestore,
  onToggleOccurrence,
}: EventMaterialsFormSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const visibleFileCount = getVisibleEventFileCount(files);
  const occurrenceIndexes = Array.from(
    { length: Math.max(1, recurrenceCount) },
    (_, index) => index
  );

  return (
    <Card data-testid="event-materials-form-section">
      <CardHeader className="space-y-2">
        <CardTitle>Materials</CardTitle>
        <div className="h-[1px] bg-[#F9F8F8]/15 border-0" />
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="rounded-md border border-dashed border-purple-300/70 bg-slate-700/50 p-4">
          <button
            type="button"
            className="flex min-h-28 w-full flex-col items-center justify-center gap-3 text-center text-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isLoading || visibleFileCount >= EVENT_FILE_MAX_COUNT}
            onClick={() => fileInputRef.current?.click()}
          >
            <FilePdfIcon size={32} weight="regular" />
            <span className="text-body font-medium">
              Add PDFs ({visibleFileCount}/{EVENT_FILE_MAX_COUNT})
            </span>
            <span className="text-small text-slate-300">
              PDF only, up to {EVENT_FILE_MAX_SIZE_BYTES / (1024 * 1024)} MB
              each
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            tabIndex={-1}
            disabled={isLoading || visibleFileCount >= EVENT_FILE_MAX_COUNT}
            onChange={(event) => {
              if (event.target.files) {
                onAddFiles(event.target.files);
                event.target.value = '';
              }
            }}
            data-testid="event-materials-input"
          />
        </div>

        {errors.length > 0 && <FieldError errors={errors} />}

        {files.length === 0 ? (
          <p className="text-body text-slate-300">No materials added.</p>
        ) : (
          <div className="space-y-3">
            {files.map((file) => {
              const isDeleted = !!file.markedForDeletion;

              return (
                <div
                  key={file.id}
                  className={`rounded-md border p-3 ${
                    isDeleted
                      ? 'border-red-400/50 bg-red-950/20 opacity-70'
                      : 'border-slate-500 bg-slate-700/70'
                  }`}
                  data-testid="event-material-row"
                >
                  <div className="flex items-start gap-3">
                    <FilePdfIcon
                      className="mt-2 flex-shrink-0 text-red-300"
                      size={24}
                      weight="fill"
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Input
                        value={file.displayTitle}
                        onChange={(event) =>
                          onTitleChange(file.id, event.target.value)
                        }
                        disabled={isDeleted || isLoading}
                        aria-label={`Title for ${file.originalFilename}`}
                        className="border-purple-400"
                      />
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-small text-slate-300">
                        <span className="truncate">
                          {file.originalFilename}
                        </span>
                        <span>{formatEventFileSize(file.sizeBytes)}</span>
                      </div>
                      {file.error && <FieldError errors={[file.error]} />}
                      {isRecurringCreate && !isDeleted && (
                        <fieldset className="space-y-2">
                          <legend className="text-small font-medium text-slate-50">
                            Add to occurrences
                          </legend>
                          <div className="flex flex-wrap gap-2">
                            {occurrenceIndexes.map((index) => {
                              const checked =
                                file.targetOccurrenceIndexes.includes(index);

                              return (
                                <label
                                  key={index}
                                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-small ${
                                    checked
                                      ? 'border-purple-300 bg-purple-300 text-slate-900'
                                      : 'border-slate-500 bg-slate-600 text-slate-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      onToggleOccurrence(file.id, index)
                                    }
                                    className="sr-only"
                                  />
                                  #{index + 1}
                                </label>
                              );
                            })}
                          </div>
                        </fieldset>
                      )}
                    </div>
                    {isDeleted && onRestore ? (
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => onRestore(file.id)}
                        disabled={isLoading}
                      >
                        Restore
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-red-300 hover:text-red-200"
                        aria-label={`Remove ${file.displayTitle}`}
                        onClick={() => onRemove(file.id)}
                        disabled={isLoading}
                      >
                        <TrashIcon size={20} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
